import { SeoController } from '@application/components';
import type { Route } from '@application/types';
import { useRouter } from '@application/utils';
import type { LocationLinksLocationsQuery } from '@gql/graphql';
import { CountryCode, ProfessionalTypeEnum } from '@gql/graphql';
import type { LocationLinksLink, LocationLinksPageData } from '@presentation/pages';
import { LocationLinksPage } from '@presentation/pages';
import { PageSkeleton } from '@presentation/pages/LocationLinks/components/PageSkeleton';
import { groupBy } from 'lodash/fp';
import { useMemo } from 'react';
import { useLocations } from './api/useLocations';
import { TRANSLATED_BE_REGIONS } from './constants';

export interface LocationLinksPageControllerProps {
  proType: ProfessionalTypeEnum;
}

export const LocationLinksPageController = (props: LocationLinksPageControllerProps) => {
  const { proType } = props;
  const { translate, routeLocationDescriptor, language, countryCode } = useRouter();
  const { locations, locationsLoading } = useLocations(proType);

  const sections = useMemo((): LocationLinksPageData['sections'] => {
    let groupedByRegion: {
      [key: string]: (LocationLinksLocationsQuery['professionalLocations'][number] | null)[];
    } = {};

    // We are mapping unique BE regions, as backend returns mixed keys in fr and nl
    if (countryCode === CountryCode.BE) {
      Object.values(TRANSLATED_BE_REGIONS).forEach((region) => {
        groupedByRegion[region[language]] = locations.filter(
          (l) => l?.region === region.fr || l?.region === region.nl,
        );
      });
    } else {
      groupedByRegion = groupBy(
        (entry) => entry?.region,
        locations.filter((x) => !!x?.region),
      );
    }

    const regions = Object.keys(groupedByRegion)
      .filter((x) => !!x)
      .sort();

    return regions.map((region) => ({
      title: region,
      links: groupedByRegion[region]
        .filter((x) => x?.city)
        .map((entry) => {
          let route: Route = 'vetList';
          if (proType === ProfessionalTypeEnum.EDUCATOR) {
            route = 'educatorList';
          } else if (proType === ProfessionalTypeEnum.GROOMER) {
            route = 'groomerList';
          }
          const encodedCityName = encodeURI(entry?.city ?? '');
          const cityName = entry?.city;
          return {
            to: routeLocationDescriptor(route, { params: [encodedCityName] }),
            label: cityName,
          } as LocationLinksLink;
        }),
    }));
  }, [countryCode, language, locations, proType, routeLocationDescriptor]);

  const seoTitle = useMemo(() => {
    return translate(`links.${proType.toLowerCase()}.seo.title`);
  }, [translate, proType]);

  const seoDescription = useMemo(() => {
    return translate(`links.${proType.toLowerCase()}.seo.description`);
  }, [translate, proType]);

  return (
    <>
      <SeoController title={seoTitle} description={seoDescription} />
      {locationsLoading && <PageSkeleton />}
      <LocationLinksPage sections={sections} proType={proType} />
    </>
  );
};
